import { ListService, PagedResultDto } from '@abp/ng.core';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  CategoryTreeNode,
  mapCategoriesTreeNode,
} from '../../categories/models/category-tree-node.model';
import { CategoryDto, CategoryService } from '@proxy/register-service/categories';
import { debounceTime, finalize, map } from 'rxjs';
import { DateAdapter } from '@abp/ng.theme.shared/extensions';
import { NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup } from '@angular/forms';
import { AssetType } from '@proxy/register-service/assets';

@Component({
  selector: 'app-category-picker',
  templateUrl: './category-picker.component.html',
  styleUrls: ['./category-picker.component.scss'],
  providers: [ListService, { provide: NgbDateAdapter, useClass: DateAdapter }],
})
export class CategoryPickerComponent implements OnInit {
  @Input() name: string | null = null;
  @Input() categoryId: string | null = null;
  @Input() readonly: boolean = false;
  @Input() knownAsOnly = false;
  @Input() withInput = false;
  @Input() asButton = false;
  @Input() form: FormGroup = new FormGroup({
    term: new FormControl(),
  });

  @Output() nodeSelected = new EventEmitter<CategoryTreeNode>();
  expandedKeys: any[] = [];
  selectedCategory?: CategoryTreeNode | null = null;
  selectedCategoryTemp?: CategoryTreeNode | null = null;
  isModalOpen = false;
  isLoading = false;
  data: PagedResultDto<CategoryTreeNode> = {
    items: [],
    totalCount: 0,
  };

  constructor(
    public readonly list: ListService,
    public readonly service: CategoryService,
  ) {}

  ngOnInit(): void {
    this.form.addControl('term', new FormControl());
    this.form
      .get('term')!
      .valueChanges.pipe(debounceTime(250))
      .subscribe(() => this.list.get());

    if (this.knownAsOnly) {
      this.form.get('assetType')?.valueChanges.subscribe({
        next: () => {
          this.onRemove();
        },
      });
    }

    this.list.hookToQuery(this.getData).subscribe(this.setData);
  }

  onSelectedNodeChange($event: CategoryTreeNode) {
    this.selectedCategoryTemp = $event;
  }

  getData = () => {
    this.isLoading = true;
    const term = this.form.get('term')?.value;

    if (term) {
      return this.service.getSearchedList(term).pipe(
        map(
          result =>
            ({
              totalCount: result.items?.length ?? 0,
              items: (result.items ?? []).map(item => ({
                ...item,
                name: (item.fullName ?? item.name ?? '').replace(/,/g, ' >'),
              })) as unknown as CategoryDto[],
            }) as PagedResultDto<CategoryDto>,
        ),
        finalize(() => (this.isLoading = false)),
      );
    }

    return this.service
      .getList({
        show: true,
        isKnownAs: this.knownAsOnly ? this.isCategoryKnownAs() : undefined,
        skipCount: 0,
        maxResultCount: 100,
      })
      .pipe(finalize(() => (this.isLoading = false)));
  };

  setData = (list: PagedResultDto<CategoryDto>, isChilds?: boolean) => {
    if (isChilds) {
      this.data = {
        ...this.data,
        totalCount: (this.data.totalCount ?? 0) + (list.totalCount ?? 0),
        items: [...(this.data.items ?? []), ...mapCategoriesTreeNode(list.items ?? [])],
      };

      this.expandedKeys.push(list.items?.[0]?.parentId);
    } else {
      this.data = new PagedResultDto<CategoryTreeNode>({
        totalCount: list.totalCount,
        items: mapCategoriesTreeNode(list.items ?? []),
      });
    }
  };

  onSelectClicked() {
    this.name = null;
    this.categoryId = null;
    this.isModalOpen = true;

    this.list.get();
  }

  toggleNodeExpansion(key: string): void {
    this.isLoading = true;

    this.service
      .getList({
        term: this.form.get('term')?.value,
        show: true,
        isKnownAs: this.knownAsOnly ? this.isCategoryKnownAs() : undefined,
        skipCount: 0,
        parentId: key,
        maxResultCount: 100,
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(e => this.setData(e, true));
  }

  private isCategoryKnownAs(): boolean | undefined {
    const value = this.form.get('assetType')?.value;

    return [AssetType.HIRED, AssetType.LEASED, AssetType.BORROWED].includes(value)
      ? true
      : undefined;
  }

  onSelect() {
    this.selectedCategory = this.selectedCategoryTemp;
    this.selectedCategoryTemp = null;
    this.name = this.selectedCategory?.name ?? null;
    this.categoryId = this.selectedCategory?.id ?? null;
    this.isModalOpen = false;
    this.nodeSelected.emit(this.selectedCategory ?? undefined);
  }

  onCancel() {
    this.name = '';
    this.categoryId = '';
    this.selectedCategoryTemp = null;
    this.isModalOpen = false;
  }

  onRemove() {
    this.selectedCategory = null;
    this.selectedCategoryTemp = null;
    this.name = null;
    this.categoryId = null;
    this.nodeSelected.emit(undefined);
  }
}
